import {
	IconButton,
	Menu,
	MenuHandler,
	MenuItem,
	MenuList,
	Typography
} from "@material-tailwind/react";
import { ReactNode } from "react";
import { mtProps } from "../../../util/mtProps";
import prestoLogo from "../../../assets/images/prestoLogo.png";

interface HeaderProps {
	showMenuButton?: boolean;
	onMenuClick?: () => void;
	rightContent?: ReactNode;
}

export const Header = ({
	showMenuButton = false,
	onMenuClick,
	rightContent
}: HeaderProps) => {
	return (
		<header className="w-full bg-gradient-to-r from-[#1a2332] via-primary to-[#2a4060] shadow-lg shadow-primary/25 px-3 sm:px-4 flex items-center justify-between h-[56px] sm:h-[64px] flex-shrink-0 z-40">
			<div className="flex items-center gap-2 sm:gap-4">
				{showMenuButton && (
					<button
						className="lg:hidden text-white/80 p-2 -ml-1 rounded-xl hover:bg-white/10 hover:text-white active:scale-95 transition-all duration-200 flex items-center justify-center"
						onClick={onMenuClick}
						aria-label="Open menu"
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							className="h-6 w-6"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
							strokeWidth={2}
							aria-hidden="true"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M4 6h16M4 12h16M4 18h16"
							/>
						</svg>
					</button>
				)}
				<img src={prestoLogo} alt="Presto Logo" className="h-8 sm:h-12 w-auto drop-shadow-lg" />
				<Typography {...mtProps} variant="h5" className="text-white font-semibold text-base sm:text-xl tracking-tight drop-shadow-sm">
					PrestoTools
				</Typography>
			</div>
			{rightContent}
		</header>
	);
};

interface UserMenuProps {
	profile: {
		name?: string;
		picture?: string;
		email?: string;
	} | null;
	onLogout: () => void;
}

export const UserMenu = ({ profile, onLogout }: UserMenuProps) => {
	const getInitials = (name: string) => {
		return name
			.split(" ")
			.map((n) => n[0])
			.join("")
			.toUpperCase()
			.slice(0, 2);
	};

	const initials = profile?.name ? getInitials(profile.name) : "U";

	return (
		<Menu {...mtProps}>
			<MenuHandler>
				<button
					type="button"
					className="w-10 h-10 rounded-full flex items-center justify-center cursor-pointer hover:opacity-80 active:scale-95 transition-all duration-200 shadow-lg overflow-hidden"
					aria-label={`${initials}, User menu`}
					aria-haspopup="menu"
				>
					{profile?.picture ? (
						<img
							src={profile.picture}
							alt={profile.name || "User"}
							className="w-full h-full object-cover"
							referrerPolicy="no-referrer"
						/>
					) : (
						<span className="text-white font-semibold text-sm">
							{initials}
						</span>
					)}
				</button>
			</MenuHandler>
			<MenuList
				{...mtProps}
				className="dark:bg-gray-800 dark:border-gray-700/50 shadow-2xl rounded-xl border-gray-200/50 min-w-[200px]"
			>
				{profile?.name && (
					<MenuItem
						{...mtProps}
						disabled
						className="font-semibold text-gray-900 dark:text-gray-100"
					>
						{profile.name}
					</MenuItem>
				)}
				{profile?.email && (
					<MenuItem
						{...mtProps}
						disabled
						className="text-gray-400 dark:text-gray-500 text-xs"
					>
						{profile.email}
					</MenuItem>
				)}
				<hr className="my-1 border-gray-100 dark:border-gray-700/50" />
				<MenuItem
					{...mtProps}
					onClick={onLogout}
					className="text-red-500 dark:text-red-400 hover:!bg-red-50 dark:hover:!bg-red-900/20 rounded-lg font-medium"
				>
					Logout
				</MenuItem>
			</MenuList>
		</Menu>
	);
};
